import type { SalesOrder } from '..';
import { getRandomNonEmptyText, getRandomOrderNumber } from '../mock';
import NonEmptyText from '../non-empty-text';
import NonNegativeNumber from '../non-negative-number';
import Currency from './currency';
import Customer from './customer';
import Order from './order';
import OrderType from './order-type';
import StakeholderType from './stakeholder-type';
import State from './state';
import Status from './status';
import Store from './store';
import Tag from './tag';
import Totals from './totals';
import UUID from './uuid';
import Workflow from './workflow';

export function getCustomer(): Customer {
	return new Customer({
		key: new NonEmptyText(`CST ${getRandomOrderNumber().value}`),
		name: getRandomNonEmptyText(2, 3),
		stakeholderType: getStakeholderType(),
		store: getStore(),
	});
}

export function getCustomers(count = 20): Customer[] {
	const customers: Customer[] = [];
	for (let i = 0; i < count; i++) {
		customers.push(getCustomer());
	}
	return customers;
}

export function getOrderNumber(): NonEmptyText {
	return new NonEmptyText(`ORD ${getRandomOrderNumber().value}`);
}

export function getOrders(count: number): SalesOrder[] {
	const orders: Order[] = [];
	for (let i = 0; i < count; i++) {
		orders.push(getOrder());
	}
	return orders;
}

function getOrder(): Order {
	return new Order({
		currency: new Currency({
			key: new NonEmptyText('DKK'),
			store: getStore(),
		}),
		customer: getCustomer(),
		orderDate: getRandomDate(),
		orderNumber: getOrderNumber(),
		orderType: getOrderType(),
		stakeholders: [],
		status: getStatus(),
		tags: getTags(),
		totals: getTotals(),
		workflow: getWorkflow(),
	});
}

const orderTypes = [
	{
		key: 'TRÆ',
		name: 'Træ',
	},
	{
		key: 'ALU',
		name: 'Aluminimum',
	},
	{
		key: 'TRÆ/ALU',
		name: 'Træ / Aluminimum',
	},
	{
		key: 'COM',
		name: 'Composite',
	},
	{
		key: 'TRÆ/COM',
		name: 'Træ / Composite',
	},
	{
		key: 'ALU/COM',
		name: 'Aluminium / Composite',
	},
] as const;

export function getCurrency(value: 'DKK' | 'SEK' | 'USD' = 'DKK'): Currency {
	const store = getStore();
	return new Currency({
		key: new NonEmptyText(value),
		store,
	});
}

export function getNewSalesOrderFromData(customer: Customer, orderType: OrderType, tags: Tag[]): Order {
	const currency = getCurrency();
	const orderNumber = getOrderNumber();
	const status = getStatus();
	const totals = getTotals();
	const workflow = getWorkflow();
	return new Order({
		currency,
		customer,
		orderDate: new Date(),
		orderNumber,
		orderType,
		stakeholders: [],
		status,
		tags,
		totals,
		workflow,
	});
}

export function getOrderType(): OrderType {
	const { key, name } = getRandomType();
	return new OrderType({
		key: new NonEmptyText(key),
		name: new NonEmptyText(name),
		store: getStore(),
	});
}

export function getOrderTypes(): OrderType[] {
	const typesArray: OrderType[] = [];
	for (const type of orderTypes) {
		typesArray.push(
			new OrderType({
				key: new NonEmptyText(type.key),
				name: new NonEmptyText(type.name),
				store: getStore(),
			}),
		);
	}
	return typesArray;
}

export function getStatus(): Status {
	return new Status({
		state: getState(),
		states: [],
		workflow: getWorkflow(),
	});
}

export function getStore(): Store {
	return new Store({
		key: new NonEmptyText('STORE 1234'),
	});
}

export function getTotals(): Totals {
	return new Totals({
		amount: new NonNegativeNumber(7_500),
		discount: new NonNegativeNumber(0.5),
		subtotal: new NonNegativeNumber(Math.round(Math.random() * 60_000 + 5_000)),
		vat: new NonNegativeNumber(1_500),
	});
}

export function getWorkflow(): Workflow {
	return new Workflow({
		uuid: new UUID('f47ac10b-58cc-4372-a567-0e02b2c3d479'),
	});
}

function getRandomDate(): Date {
	const ONE_SECONDS = 1000;
	const ONE_MINUTE = 60 * ONE_SECONDS;
	const ONE_HOUR = 60 * ONE_MINUTE;
	const ONE_DAY = 24 * ONE_HOUR;
	const ONE_WEEK = 7 * ONE_DAY;
	const ONE_MONTH = 4.5 * ONE_WEEK;
	const THREE_MONTHS = 3 * ONE_MONTH;
	const RANDOM_FUTURE_DATE = Date.now() + Math.round(Math.random() * THREE_MONTHS);
	return new Date(RANDOM_FUTURE_DATE);
}

function getRandomType(): { key: string; name: string } {
	const length = orderTypes.length;
	const index = Math.floor(Math.random() * length);
	return orderTypes[index] as { key: string; name: string };
}

function getStakeholderType(): StakeholderType {
	return new StakeholderType({
		key: new NonEmptyText('Debitor'),
	});
}

function getState(): State {
	return new State({
		key: getRandomNonEmptyText(1, 1),
		workflow: getWorkflow(),
	});
}

function getTag(): Tag {
	return new Tag({
		key: getRandomNonEmptyText(1, 1),
		value: getRandomNonEmptyText(1, 1),
	});
}

function getTags(): Tag[] {
	const tags: Tag[] = [];
	const count = Math.round(Math.random() * 4 + 1);
	for (let i = 0; i < count; i++) {
		tags.push(getTag());
	}
	return tags;
}
